/**
 * Whisper Local STT Provider
 *
 * Implements the STTProvider interface for a local Whisper STT WebSocket
 * server running on the edge device (e.g., NVIDIA Jetson Thor).
 *
 * Protocol is Deepgram-compatible:
 *   - Client sends binary PCM16 audio frames (16kHz mono)
 *   - Client sends JSON control: {"type": "KeepAlive"}, {"type": "Finalize"}, {"type": "CloseStream"}
 *   - Server sends JSON: {"type": "Results", "is_final": bool, "speech_final": bool,
 *                          "channel": {"alternatives": [{"transcript": "..."}]}, "duration": float}
 *   - Server sends JSON: {"type": "UtteranceEnd"}
 *   - Server sends JSON: {"type": "Metadata", "duration": float} on close
 *
 * Key differences from DeepgramSTTProvider:
 *   - Connects to local WebSocket URL (no cloud API)
 *   - No ephemeral token needed (local service, no auth)
 *   - Same PCM 16kHz audio format
 */

import type { LocalSTTOptions, STTConfig, STTEvents, STTProvider } from '@/types/voice'

/** The server URL with this connection's thresholds; with none set, the base unchanged */
export function localSttUrl(base: string, local?: LocalSTTOptions): string {
  const params: string[] = []
  if (local?.silenceMs !== undefined) params.push(`silence_ms=${local.silenceMs}`)
  if (local?.silenceThreshold !== undefined) params.push(`silence_threshold=${local.silenceThreshold}`)
  if (local?.maxBufferMs !== undefined) params.push(`max_buffer_ms=${local.maxBufferMs}`)
  if (params.length === 0) return base
  return `${base}${base.includes('?') ? '&' : '?'}${params.join('&')}`
}

export class WhisperLocalSTTProvider implements STTProvider {
  readonly name = 'whisper-local'
  /** the edge-local server answers every Finalize */
  readonly alwaysAnswersFinalize = true
  private ws: WebSocket | null = null
  private events: STTEvents
  private keepAliveIntervalId: ReturnType<typeof setInterval> | null = null
  private _totalDurationSec = 0
  private wsUrl: string

  constructor(events: STTEvents, wsUrl?: string) {
    this.events = events
    // Default to relative path (proxied through nginx)
    this.wsUrl = wsUrl || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/stt`
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  get totalDurationMs(): number {
    return this._totalDurationSec * 1000
  }

  private _modelUsed = 'whisper-local'
  get modelUsed(): string {
    return this._modelUsed
  }

  async connect(_config: STTConfig): Promise<void> {
    this._totalDurationSec = 0
    this._modelUsed = _config.model || 'whisper-local'

    return new Promise<void>((resolve, reject) => {
      // Connect to local WebSocket server — no auth needed
      this.ws = new WebSocket(localSttUrl(this.wsUrl, _config.local))

      this.ws.onopen = () => {
        this.events.onOpen()
        this.startKeepAlive()
        resolve()
      }

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data as string)

          if (data.type === 'Results') {
            const transcript =
              data.channel?.alternatives?.[0]?.transcript || ''

            if (data.is_final && data.duration) {
              this._totalDurationSec += data.duration
            }

            this.events.onTranscript(
              transcript,
              data.is_final ?? false,
              data.speech_final ?? false,
              data.from_finalize === true
            )
          }

          if (data.type === 'UtteranceEnd') {
            this.events.onUtteranceEnd()
          }

          if (data.type === 'Metadata' && data.duration) {
            this._totalDurationSec = data.duration
          }
        } catch {
          // Ignore unparseable messages
        }
      }

      this.ws.onerror = () => {
        this.events.onError(
          new Error('Local Whisper STT WebSocket connection error')
        )
        reject(new Error('Local Whisper STT connection failed'))
      }

      this.ws.onclose = () => {
        this.stopKeepAlive()
        this.events.onClose()
      }
    })
  }

  sendAudio(pcmData: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(pcmData)
    }
  }

  keepAlive(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'KeepAlive' }))
    }
  }

  finalize(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'Finalize' }))
    }
  }

  close(): void {
    this.stopKeepAlive()
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'CloseStream' }))
    }
    this.ws?.close()
    this.ws = null
  }

  private startKeepAlive(): void {
    this.stopKeepAlive()
    this.keepAliveIntervalId = setInterval(() => this.keepAlive(), 5000)
  }

  private stopKeepAlive(): void {
    if (this.keepAliveIntervalId) {
      clearInterval(this.keepAliveIntervalId)
      this.keepAliveIntervalId = null
    }
  }
}
